"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ProductCardItem {
  id: string;
  title: Record<Lang, string>;
  badge: Record<Lang, string>;
  description: Record<Lang, string>;
  href: string;
  specs: Record<Lang, string>;
  image: string;
  imgAlt: Record<Lang, string>;
}
const recommendedProducts: ProductCardItem[] = [{
  id: "square-mesh",
  title: {
    en: "Square Weave Wire Mesh",
    zh: "标准方孔编织网",
    es: "Malla de tejido cuadrado",
    de: "Quadratmaschengewebe",
    fr: "Toile tissée à mailles carrées",
    pt: "Tela tecida de malha quadrada"
  },
  badge: {
    en: "1 – 635 Mesh",
    zh: "1 – 635 目",
    es: "1 – 635 Mesh",
    de: "1 – 635 Mesh",
    fr: "1 – 635 Mesh",
    pt: "1 – 635 Mesh"
  },
  description: {
    en: "Precision square opening woven wire cloth engineered to ISO 9044 and ASTM E2016 for vibratory particle sizing and powder sieving.",
    zh: "参照 ISO 9044 与 ASTM E2016 规范制造的高精度方孔编织网，专用于振动筛分级与干粉粒度精确分选。",
    es: "Telas de alambre de abertura cuadrada de precisión según ISO 9044 y ASTM E2016 para tamizado vibratorio de partículas y polvos.",
    de: "Präzisions-Quadratmaschengewebe nach ISO 9044 und ASTM E2016 für Vibrationssiebung und Pulverklassierung.",
    fr: "Toile métallique à mailles carrées conforme ISO 9044 et ASTM E2016 pour criblage vibrant et calibrage de poudres.",
    pt: "Telas tecidas com abertura quadrada de precisão conforme ISO 9044 e ASTM E2016 para classificação vibratória e peneiramento de pós."
  },
  href: "/SquareMeshPage",
  specs: {
    en: "20 µm – 25 mm",
    zh: "20 µm – 25 mm",
    es: "20 µm – 25 mm",
    de: "20 µm – 25 mm",
    fr: "20 µm – 25 mm",
    pt: "20 µm – 25 mm"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
  imgAlt: {
    en: "Stainless steel precision square weave wire mesh for screening",
    zh: "用于颗粒筛分的精密不锈钢方孔编织丝网",
    es: "Malla de alambre de tejido cuadrado de acero inoxidable para tamizado",
    de: "Edelstahl-Präzisionsdrahtgewebe für Siebung",
    fr: "Toile métallique inox à mailles carrées pour criblage",
    pt: "Tela de arame inox com malha quadrada de precisão para peneiramento"
  }
}, {
  id: "sieve-bend",
  title: {
    en: "Sieve Bend Screens",
    zh: "弧形脱水筛板",
    es: "Cribas parabólicas (Sieve Bend)",
    de: "Bogensiebe (Sieve Bend)",
    fr: "Grilles courbes (Sieve Bend)",
    pt: "Peneiras curvas (Sieve Bend)"
  },
  badge: {
    en: "Parabolic Gravity Feed",
    zh: "抛物线重力自流",
    es: "Alimentación parabólica",
    de: "Schwerkraft-Bogensieb",
    fr: "Alimentation gravitaire",
    pt: "Gravidade parabólica"
  },
  description: {
    en: "Curved profile wire panels designed for high-capacity static slurry dewatering, solids recovery, and fiber sizing.",
    zh: "连续弧形型线截面筛板，专为大通量静态矿浆脱水、工业物料固液回收及纤维分级设计。",
    es: "Paneles de alambre en cuña curvados para deshidratación estática de lodos a gran capacidad y recuperación de sólidos.",
    de: "Gebogene Profildraht-Siebböden für Hochleistungs-Schlammentwässerung und statische Feststoffrückgewinnung.",
    fr: "Panneaux en fils profilés courbes pour égouttage statique de boues à haut débit et récupération de matières.",
    pt: "Painéis de arame perfilado curvos projetados para desidratação estática de alta vazão e recuperação de sólidos."
  },
  href: "/SieveBendScreensPage",
  specs: {
    en: "0.25 – 3.0 mm",
    zh: "0.25 – 3.0 mm",
    es: "0,25 – 3,0 mm",
    de: "0,25 – 3,0 mm",
    fr: "0,25 – 3,0 mm",
    pt: "0,25 – 3,0 mm"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
  imgAlt: {
    en: "Curved wedge wire sieve bend screen panel for solid liquid dewatering",
    zh: "固液脱水用弧形楔形丝网筛板",
    es: "Panel de criba curvada de alambre en cuña para separación sólido-líquido",
    de: "Edelstahl-Bogensieb für die Fest-Flüssig-Entwässerung",
    fr: "Grille courbe en fil profilé pour séparation solide-liquide",
    pt: "Painel de peneira curva em cunha para desidratação sólido-líquido"
  }
}, {
  id: "cylindrical-screens",
  title: {
    en: "Cylindrical Screen Baskets",
    zh: "圆柱筛筒与离心转鼓",
    es: "Cilindros y cestas de cribado",
    de: "Zylindrische Siebkörbe",
    fr: "Paniers et cylindres filtrants",
    pt: "Cilindros e cestos de peneiramento"
  },
  badge: {
    en: "0.02 – 5.0 mm Slot",
    zh: "0.02 – 5.0 mm 缝隙",
    es: "Ranura 0,02 – 5,0 mm",
    de: "Spalt 0,02 – 5,0 mm",
    fr: "Fente 0,02 – 5,0 mm",
    pt: "Ranhura 0,02 – 5,0 mm"
  },
  description: {
    en: "Axial and circumferential slot wedge wire cylinders engineered for screw presses, centrifugal dryers, and auto-backwash housings.",
    zh: "轴向与环向连续缝隙楔形筛筒，适用于螺旋压榨机、离心脱水机及全自动反冲洗分离系统。",
    es: "Cilindros de ranura axial y circunferencial para prensas de tornillo, secadores centrífugos y filtros automáticos.",
    de: "Axial- und Radialspaltzylinder für Schneckenpressen, Zentrifugentrockner und Rückspülfilter.",
    fr: "Cylindres à fente axiale ou circonférentielle pour presses à vis, centrifugeuses et systèmes automatiques.",
    pt: "Cilindros de ranhura axial e circunferencial para prensas de rosca, secadores centrífugos e retrolavagem."
  },
  href: "/CylindricalScreensPage",
  specs: {
    en: "High Collapse ΔP",
    zh: "高抗压差刚性",
    es: "Alta resistencia ΔP",
    de: "Hohe Druckfestigkeit",
    fr: "Haute tenue en pression",
    pt: "Alta resistência a ΔP"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/axial_slot_cylinder.webp",
  imgAlt: {
    en: "Stainless steel axial slot wedge wire screen cylinder",
    zh: "不锈钢轴向缝隙楔形丝滤筒组件",
    es: "Cilindro de criba de ranura axial de acero inoxidable",
    de: "Edelstahl-Axialspalt-Siebzylinder",
    fr: "Cylindre filtrant inox à fente axiale",
    pt: "Cilindro de tela de arame em cunha com fenda axial"
  }
}, {
  id: "screen-packs",
  title: {
    en: "Screen Packs & Disc Sets",
    zh: "组合筛片与换网网包",
    es: "Paquetes de mallas y discos",
    de: "Siebpakete & Scheibensätze",
    fr: "Packs de toiles et disques",
    pt: "Pacotes de telas e discos"
  },
  badge: {
    en: "Multi-Mesh Assembly",
    zh: "多层复合装配",
    es: "Conjunto multicapa",
    de: "Mehrlagenbaugruppe",
    fr: "Assemblage multicouche",
    pt: "Montagem multicamadas"
  },
  description: {
    en: "Pre-assembled spot-welded, rimmed, or ultrasonic-welded screen discs configured for vibratory separators and pressurized fluid housings.",
    zh: "点焊、金属包边或超声波预装配滤片组件，适配各类高频振动筛分机与承压流体过滤装置。",
    es: "Discos de criba premontados con soldadura por puntos o bordeados para separadores vibratorios y carcasas presurizadas.",
    de: "Vorkonfektionierte punktgeschweißte oder eingefasste Siebscheiben für Vibrationssiebmaschinen und Druckfilter.",
    fr: "Disques de tamisage pré-assemblés soudés par points ou sertis pour tamiseurs vibrants et carters sous pression.",
    pt: "Discos pré-montados com solda ponto ou emoldurados para separadores vibratórios e carcaças sob pressão."
  },
  href: "/ScreenPacksPage",
  specs: {
    en: "Calibrated Microns",
    zh: "精密校准微米",
    es: "Micras calibradas",
    de: "Kalibrierte Mikron",
    fr: "Microns calibrés",
    pt: "Mícrons calibrados"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
  imgAlt: {
    en: "Pre-assembled stainless steel wire mesh screen packs for separation equipment",
    zh: "分离设备用预装配不锈钢组合筛网片",
    es: "Paquetes de malla de alambre de acero inoxidable para equipos de separación",
    de: "Vorkonfektionierte Edelstahl-Drahtgewebe-Siebpakete für Separationsanlagen",
    fr: "Packs de toiles métalliques inox pour équipements de séparation",
    pt: "Pacotes de tela em malha de aço inoxidável para equipamentos de separação"
  }
}];
interface Copy {
  eyebrow: string;
  heading: string;
  lead: string;
  exploreLink: string;
}
const copyData: Record<Lang, Copy> = {
  en: {
    eyebrow: "Product Configurations",
    heading: "Recommended Products for Screening & Separation",
    lead: "Engineered mesh structures and fabricated separation components tailored for particle classification, continuous solids dewatering, and severe fluid demisting.",
    exploreLink: "View Specification"
  },
  zh: {
    badge: "适配产品配置",
    eyebrow: "适配产品配置",
    heading: "筛分与固液/气液分离推荐产品",
    lead: "针对颗粒分选、连续矿浆脱水与严苛工况气液除沫定制工程级不锈钢丝网与连续缝隙楔形网构件。",
    exploreLink: "查看规格参数"
  },
  es: {
    eyebrow: "Configuraciones de producto",
    heading: "Productos recomendados para tamizado y separación",
    lead: "Estructuras de malla y componentes de separación a medida para clasificación de partículas, deshidratación continua y desgasificación severa.",
    exploreLink: "Ver especificación"
  },
  de: {
    eyebrow: "Produktkonfigurationen",
    heading: "Empfohlene Produkte für Siebung und Separation",
    lead: "Präzisions-Gewebe und Abscheidekomponenten für Partikelklassierung, Schlammentwässerung und Tropfenabscheidung.",
    exploreLink: "Spezifikation ansehen"
  },
  fr: {
    eyebrow: "Configurations produits",
    heading: "Produits recommandés pour le criblage et la séparation",
    lead: "Structures métalliques et composants de séparation conçus pour le calibrage de particules, l'égouttage continu et le dévésiculage.",
    exploreLink: "Voir les spécifications"
  },
  pt: {
    eyebrow: "Configurações de produtos",
    heading: "Produtos recomendados para peneiramento e separação",
    lead: "Estruturas de malha e componentes de separação projetados para classificação de partículas, desidratação contínua e desembaçamento severo.",
    exploreLink: "Ver especificação"
  }
};
export default function RecommendedProducts() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  return <section id="recommendedproducts" data-nav="light" className="bg-background mf-section border-b border-border text-foreground">
      <div className="mf-container">
        {/* Section Heading & Short Lead */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-sm font-mono uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                {c.eyebrow}
              </span>
              <h2 className="font-bold text-foreground font-default tracking-tight mf-h2">
                {c.heading}
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md mf-lead">
              {c.lead}
            </p>
          </div>
        </Reveal>

        {/* 4 Recommended Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recommendedProducts.map((item, index) => <Reveal key={item.id} delay={Math.min(index * 80, 300)} className="h-full">
              <Link to={item.href} className="group flex flex-col justify-between h-full bg-card border border-border rounded-lg overflow-hidden mf-card-hover">
                <div>
                  <div className="mf-card-media mf-ratio-card w-full relative mf-img-zoom">
                    <img src={item.image} alt={pick(lang, item.imgAlt)} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  </div>

                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Badge variant="outline" className="font-mono text-xs font-normal tracking-tight bg-muted text-foreground">
                        {pick(lang, item.badge)}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {pick(lang, item.specs)}
                      </span>
                    </div>

                    <h3 className="font-semibold text-foreground font-default mb-2 text-lg mf-card-title transition-colors">
                      {pick(lang, item.title)}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {pick(lang, item.description)}
                    </p>
                  </div>
                </div>

                <div className="px-5 py-3.5 border-t border-border bg-card flex items-center justify-between text-sm font-medium text-primary">
                  <span>{c.exploreLink}</span>
                  <ArrowRight className="size-4 mf-arrow-nudge text-primary" />
                </div>
              </Link>
            </Reveal>)}
        </div>
      </div>
    </section>;
}